import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { NoteListOptions } from "@shared-core/data/adapter";
import type * as API from "@shared-core/data/api-types";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { formatCount } from "@shared-core/domain/formatters";
import { NoteCard } from "../app/components/domain/customers/NoteCard";
import { NoteComposer } from "../app/components/domain/customers/NoteComposer";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";

const PAGE_SIZE = 25;

export function NotesPage() {
  const dataAdapter = useDataAdapter();
  const [searchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") ?? undefined;
  // The discrete ticket/opportunity/alert query params map onto the generic
  // connection (subject_type, subject_id) — first one present wins. The
  // subject_type value stays "task" (the historical literal wf_note rows
  // carry for a ticket connection, unchanged by the workflow_module
  // Task->Ticket rename); only the URL param name follows the new noun.
  const ticketFilter = searchParams.get("ticket") ?? undefined;
  const opportunityFilter = searchParams.get("opportunity") ?? undefined;
  const alertFilter = searchParams.get("alert") ?? undefined;
  const subjectType = ticketFilter
    ? "task"
    : opportunityFilter
      ? "opportunity"
      : alertFilter
        ? "alert"
        : undefined;
  const subjectId =
    ticketFilter ?? opportunityFilter ?? alertFilter ?? undefined;

  const [sort, setSort] = useState<string>("-created_at");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const { state, retry } = useListQuery(() => {
    const options: NoteListOptions = { sort, page, pageSize: PAGE_SIZE };
    if (customerFilter) options.customerId = customerFilter;
    if (subjectType) options.subjectType = subjectType;
    if (subjectId) options.subjectId = subjectId;
    if (q.trim()) options.q = q.trim();
    return dataAdapter.listNotes(options);
  }, [dataAdapter, sort, q, page, customerFilter, subjectType, subjectId]);

  useEffect(() => {
    setPage(1);
  }, [sort, q, customerFilter, subjectType, subjectId]);

  const notes: API.Note[] = state.status === "success" ? state.data.items : [];
  const total = state.status === "success" ? state.data.total : 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl mb-1">Notes</h1>
          <p className="text-sm text-muted-foreground">
            {customerFilter
              ? "Notes for this customer"
              : "All notes across your portfolio"}
          </p>
        </div>
        {/* A ticket's notes stay readable, but no new note attaches to a
            ticket (Ben, 16 Oct): a ticket comment is the one way to note on one. */}
        {customerFilter && !ticketFilter && (
          <NoteComposer
            subjectType={subjectType}
            subjectId={subjectId}
            subjectLabel={undefined}
            customerId={customerFilter}
            jobPickerCustomerId={subjectType ? undefined : customerFilter}
            onCreated={retry}
          />
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <input
          type="text"
          placeholder="Search notes..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="px-3 py-2 border border-border rounded bg-background text-sm w-full sm:w-64"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-border bg-background"
        >
          <option value="-created_at">Newest first</option>
          <option value="created_at">Oldest first</option>
        </select>
      </div>

      {/* Status / results count */}
      <div className="text-sm text-muted-foreground">
        {state.status === "loading"
          ? "Loading..."
          : state.status === "success"
            ? `${formatCount(total)} note${total !== 1 ? "s" : ""}`
            : null}
      </div>

      {/* Error */}
      {state.status === "error" && (
        <ErrorPanel message={state.message} onRetry={retry} />
      )}

      {/* Empty */}
      {state.status === "success" && notes.length === 0 && (
        <div className="text-sm p-8 text-center border border-border rounded-lg space-y-1">
          <p className="font-medium text-foreground">No notes yet</p>
          <p className="text-muted-foreground">
            Add a note from any customer's page to start capturing context.
          </p>
        </div>
      )}

      {/* Results */}
      {state.status === "success" && notes.length > 0 && (
        <div className="space-y-3">
          {notes.map((note) => (
            <NoteCard key={note.id} note={note} compact />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
